import type { ApiFetchPriority } from "./apiFetchAdmission";

export type ApiFetchTimeline = {
  id: number;
  method: string;
  path: string;
  priority: ApiFetchPriority;
  admissionQueuedAt: number | null;
  admissionGrantedAt: number | null;
  fetchCalledAt: number | null;
  headersReceivedAt: number | null;
  bodyCompletedAt: number | null;
  headerDeadlineFiredAt: number | null;
  totalDeadlineFiredAt: number | null;
  callerAbortedAt: number | null;
  outcome: "active" | "completed" | "header-deadline" | "total-deadline" | "caller-aborted" | "error";
  nextHopProtocol: string | null;
  responseStart: number | null;
};

const active = new Map<number, ApiFetchTimeline>();
const recent: ApiFetchTimeline[] = [];
let nextId = 1;

export function beginApiFetchTimeline(
  method: string,
  path: string,
  priority: ApiFetchPriority,
): ApiFetchTimeline {
  const timeline: ApiFetchTimeline = {
    id: nextId++,
    method,
    path,
    priority,
    admissionQueuedAt: Date.now(),
    admissionGrantedAt: null,
    fetchCalledAt: null,
    headersReceivedAt: null,
    bodyCompletedAt: null,
    headerDeadlineFiredAt: null,
    totalDeadlineFiredAt: null,
    callerAbortedAt: null,
    outcome: "active",
    nextHopProtocol: null,
    responseStart: null,
  };
  active.set(timeline.id, timeline);
  return timeline;
}

export function updateApiFetchTimeline(
  timeline: ApiFetchTimeline,
  patch: Partial<ApiFetchTimeline>,
): void {
  Object.assign(timeline, patch);
}

export function finishApiFetchTimeline(timeline: ApiFetchTimeline): void {
  active.delete(timeline.id);
  recent.unshift({ ...timeline });
  if (recent.length > 30) recent.length = 30;
}

export function resourceTimingForUrl(url: string): {
  nextHopProtocol: string | null;
  responseStart: number | null;
} {
  if (typeof performance === "undefined") {
    return { nextHopProtocol: null, responseStart: null };
  }
  const entries = performance.getEntriesByName(url, "resource") as PerformanceResourceTiming[];
  const entry = entries.at(-1);
  return {
    nextHopProtocol: entry?.nextHopProtocol || null,
    responseStart:
      entry != null && Number.isFinite(entry.responseStart) && entry.responseStart > 0
        ? Math.round(entry.responseStart)
        : null,
  };
}

export function getApiFetchTimelineSnapshot(): {
  active: ApiFetchTimeline[];
  recent: ApiFetchTimeline[];
} {
  return {
    active: [...active.values()].map((entry) => ({ ...entry })),
    recent: recent.map((entry) => ({ ...entry })),
  };
}

export function resetApiFetchTelemetryForTests(): void {
  active.clear();
  recent.length = 0;
  nextId = 1;
}
